import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { usePreferenceManager } from '@/hooks/usePreferenceManager';
import { useModalManager } from '@/hooks/useModalManager';
import { ModalId } from '@/types/modal-manager.types';
import { ProfilePopupProps, MOOD_OPTIONS, UserMood } from '@/types/dashboard';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { KeyValueRow } from '@/components/ui/key-value-row';
import { FadeIn } from '@/components/AnimatedElements';
import { Avatar } from '@/components/ui/avatar';
import { useThemeColors } from '@/styles';
import { useTheme } from '@/styles/theme';
import { ThemeVariant, encodeThemeVariant } from '@/styles/theme';
import { useSizing } from '@/styles/sizing';
import {
  usePlayful,
  getAllPlayfulThemes,
  PlayfulThemeName,
} from '@/styles/playful';
import { getAllThemes } from '@/styles/colors/themes';
import { SizingLevel, SIZING_SCALES } from '@/types/sizing';
import { Icon, IconName } from '@/icons';
import { shadows } from '@/styles/design-tokens';
import { withOpacity } from '@/styles/colors/withOpacity';
import { MASCOT_COMPONENTS } from '@/components/core/ThemeMascot/mascots';
import { ProfilePictureSelector } from './ProfilePictureSelector';

// Sizing options configuration
const SIZING_OPTIONS: Array<{ id: SizingLevel; label: string }> = [
  { id: SizingLevel.EXTRA_SMALL, label: 'XS' },
  { id: SizingLevel.SMALL, label: 'S' },
  { id: SizingLevel.MEDIUM, label: 'M' },
  { id: SizingLevel.LARGE, label: 'L' },
  { id: SizingLevel.EXTRA_LARGE, label: 'XL' },
];

const VARIANT_OPTIONS: Array<{
  id: ThemeVariant;
  label: string;
  filter: string;
}> = [
  { id: ThemeVariant.LIGHT, label: 'Light', filter: 'brightness(1.0)' },
  { id: ThemeVariant.MEDIUM, label: 'Medium', filter: 'brightness(0.65)' },
  {
    id: ThemeVariant.DARK,
    label: 'Dark',
    filter: 'brightness(0.25) saturate(1.5)',
  },
];

export default function ProfilePopup({
  isOpen,
  onClose,
  onLogout,
}: ProfilePopupProps) {
  const { user, updateUser } = useAuth();
  const { state: preferenceState, actions: preferenceActions } =
    usePreferenceManager();
  const colors = useThemeColors();
  const { theme, setTheme, variant, setVariant } = useTheme();
  const { sizingLevel, setSizingLevel } = useSizing();
  const { playfulTheme, setPlayfulTheme, soundEnabled } = usePlayful();
  const { openModal, closeModal } = useModalManager();
  const [currentMood, setCurrentMood] = useState<UserMood>(MOOD_OPTIONS[0]);
  const [isProfileExpanded, setIsProfileExpanded] = useState(false);
  const [hoveredTheme, setHoveredTheme] = useState<string | null>(null);
  const [isAvatarHovered, setIsAvatarHovered] = useState(false);
  const [showPictureSelector, setShowPictureSelector] = useState(false);

  // Track initial values to detect changes
  const [initialTheme, setInitialTheme] = useState<string | null>(null);
  const [initialVariant, setInitialVariant] = useState<ThemeVariant | null>(
    null
  );
  const [initialMood, setInitialMood] = useState<string | null>(null);
  const [initialSizing, setInitialSizing] = useState<SizingLevel | null>(null);
  const [initialPlayfulTheme, setInitialPlayfulTheme] = useState<string | null>(
    null
  );
  const [initialSoundEnabled, setInitialSoundEnabled] = useState<
    boolean | null
  >(null);
  const [hasChanges, setHasChanges] = useState(false);

  // Auto-generate theme options from registry
  const themeOptions = getAllThemes().map(themeData => ({
    id: themeData.name,
    label: themeData.metadata.label,
    iconName: themeData.metadata.icon,
    preview: themeData.metadata.preview,
  }));

  // Track modal open/close state
  useEffect(() => {
    if (isOpen) {
      openModal(ModalId.PROFILE);
    } else {
      closeModal(ModalId.PROFILE);
    }
  }, [isOpen, openModal, closeModal]);

  // Load saved mood from preference data (centralized in preference manager)
  useEffect(() => {
    if (preferenceState.preferences?.mood) {
      const foundMood = MOOD_OPTIONS.find(
        m => m.id === preferenceState.preferences?.mood
      );
      if (foundMood) {
        setCurrentMood(foundMood);
      }
    }
  }, [preferenceState.preferences]);

  // Capture initial values when popup opens (only once per open)
  useEffect(() => {
    if (isOpen) {
      setInitialTheme(theme);
      setInitialVariant(variant);
      setInitialMood(currentMood.id);
      setInitialSizing(sizingLevel);
      setInitialPlayfulTheme(playfulTheme);
      setInitialSoundEnabled(soundEnabled);
      setHasChanges(false);
    }
    // Note: Intentionally only depend on isOpen to capture values once per open
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Track changes to theme, variant, mood, and sizing
  useEffect(() => {
    if (
      initialTheme !== null &&
      initialVariant !== null &&
      initialMood !== null &&
      initialSizing !== null &&
      initialPlayfulTheme !== null &&
      initialSoundEnabled !== null
    ) {
      const themeChanged = theme !== initialTheme;
      const variantChanged = variant !== initialVariant;
      const moodChanged = currentMood.id !== initialMood;
      const sizingChanged = sizingLevel !== initialSizing;
      const playfulChanged = playfulTheme !== initialPlayfulTheme;
      const soundChanged = soundEnabled !== initialSoundEnabled;
      setHasChanges(
        themeChanged ||
          variantChanged ||
          moodChanged ||
          sizingChanged ||
          playfulChanged ||
          soundChanged
      );
    }
  }, [
    theme,
    variant,
    currentMood.id,
    sizingLevel,
    playfulTheme,
    soundEnabled,
    initialTheme,
    initialVariant,
    initialMood,
    initialSizing,
    initialPlayfulTheme,
    initialSoundEnabled,
  ]);

  // Reset profile expansion state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      setIsProfileExpanded(false);
    }
  }, [isOpen]);

  const handleMoodChange = (mood: UserMood) => {
    setCurrentMood(mood);
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      if (hasChanges) {
        preferenceActions.syncPreferences({
          // Encode theme + variant into the single stored string
          theme: encodeThemeVariant(theme, variant) as unknown as typeof theme,
          mood: currentMood.id,
          sizing: sizingLevel,
          playful_theme: playfulTheme,
          playful_sound: soundEnabled ? 'on' : 'off',
        });
      }

      setInitialTheme(null);
      setInitialVariant(null);
      setInitialMood(null);
      setInitialSizing(null);
      setInitialPlayfulTheme(null);
      setInitialSoundEnabled(null);
      setHasChanges(false);

      onClose();
    }
  };

  const handleLogout = () => {
    onClose();
    onLogout();
  };

  if (!user) return null;

  return (
    <>
      {/* Main Profile Popup */}
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent
          className="sm:max-w-3xl lg:max-w-4xl flex flex-col max-h-[90vh]"
          style={{ backgroundColor: colors.background }}
        >
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-3">
              <div
                className="relative"
                onMouseEnter={() => setIsAvatarHovered(true)}
                onMouseLeave={() => setIsAvatarHovered(false)}
              >
                <Avatar
                  src={user.profile_image}
                  displayName={user.display_name}
                  userId={user.id}
                  size={80}
                />
                {/* Hover overlay */}
                <div
                  className="absolute inset-0 flex items-center justify-center rounded-full cursor-pointer transition-opacity"
                  style={{
                    backgroundColor: withOpacity(colors.text.primary, 0.6),
                    opacity: isAvatarHovered ? 1 : 0,
                  }}
                  onClick={() => setShowPictureSelector(true)}
                >
                  <span
                    className="text-sm font-semibold"
                    style={{ color: colors.background }}
                  >
                    Change
                  </span>
                </div>
              </div>
              <div className="flex-1">
                <div
                  className="text-lg font-semibold"
                  style={{ color: colors.text.primary }}
                >
                  {user.display_name}
                </div>
                <div
                  className="text-sm capitalize"
                  style={{ color: colors.text.secondary }}
                >
                  {user.role}
                </div>
              </div>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setIsProfileExpanded(!isProfileExpanded)}
                className="flex-shrink-0"
                aria-label="Toggle profile details"
              >
                <Icon
                  name={IconName.CHEVRON_DOWN}
                  size="sm"
                  color={colors.text.secondary}
                  className={`transition-transform duration-300 ${isProfileExpanded ? 'rotate-180' : ''}`}
                />
              </Button>
            </DialogTitle>

            {/* Collapsible Profile Information */}
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                isProfileExpanded ? 'opacity-100' : 'max-h-0 opacity-0'
              }`}
              style={{
                maxHeight: isProfileExpanded
                  ? 'calc(500px * var(--sizing-multiplier, 1))'
                  : undefined,
              }}
            >
              <div
                className="rounded-lg p-4 mt-4"
                style={{ backgroundColor: colors.offWhite }}
              >
                <div className="space-y-3">
                  <KeyValueRow
                    label="Username:"
                    value={user.username}
                    textBehavior="wrap"
                  />
                  <KeyValueRow
                    label="Organization:"
                    value={user.organization_title || 'N/A'}
                    textBehavior="marquee"
                  />
                  <KeyValueRow
                    label="Email:"
                    value={user.email || 'N/A'}
                    textBehavior="marquee"
                  />
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto space-y-8 py-4 px-1">
            {/* Current Mood Section */}
            <FadeIn direction="up" delay={100} duration={0.5}>
              <div>
                <h3
                  className="text-sm font-medium mb-6"
                  style={{ color: colors.text.primary }}
                >
                  How are you feeling today?
                </h3>
                <div className="flex overflow-x-auto gap-3 p-2 pb-4">
                  {MOOD_OPTIONS.map(mood => {
                    const isActive = currentMood.id === mood.id;

                    return (
                      <button
                        key={mood.id}
                        className="flex-shrink-0 flex flex-col items-center justify-center gap-1 rounded-md transition-all duration-200 ease-out border hover:scale-105"
                        style={{
                          height: 'calc(80px * var(--sizing-multiplier, 1))',
                          minWidth: 'calc(100px * var(--sizing-multiplier, 1))',
                          ...(isActive
                            ? {
                                backgroundColor: colors.active.background,
                                color: colors.active.text,
                                borderColor: colors.active.background,
                                transform: 'scale(1.05)',
                                boxShadow: shadows.interactive,
                              }
                            : {
                                backgroundColor: 'transparent',
                                color: colors.text.primary,
                                borderColor: colors.border.default,
                              }),
                        }}
                        onClick={() => handleMoodChange(mood)}
                      >
                        <span className="text-2xl">{mood.emoji}</span>
                        <span className="text-xs">{mood.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </FadeIn>

            {/* Sizing Selector */}
            <FadeIn direction="up" delay={150} duration={0.5}>
              <div>
                <h3
                  className="text-sm font-medium mb-6"
                  style={{ color: colors.text.primary }}
                >
                  Choose your UI size
                </h3>
                <div className="grid grid-cols-5 gap-3 p-2">
                  {SIZING_OPTIONS.map(option => {
                    const isActive = sizingLevel === option.id;
                    const scale = SIZING_SCALES[option.id];

                    return (
                      <button
                        key={option.id}
                        className={`w-full flex flex-col items-center justify-center gap-2 rounded-md transition-all duration-200 ease-out hover:scale-105 ${
                          isActive ? 'border-2' : 'border'
                        }`}
                        style={{
                          height: 'calc(80px * var(--sizing-multiplier, 1))',
                          ...(isActive
                            ? {
                                backgroundColor: colors.active.background,
                                color: colors.active.text,
                                borderColor: colors.active.background,
                                transform: 'scale(1.05)',
                                boxShadow: shadows.interactive,
                              }
                            : {
                                backgroundColor: 'transparent',
                                color: colors.text.primary,
                                borderColor: colors.border.default,
                              }),
                        }}
                        onClick={() => setSizingLevel(option.id)}
                      >
                        <Icon
                          name={IconName.A_LARGE}
                          size="sm"
                          strokeWidth={2.5}
                        />
                        <div className="flex flex-col items-center">
                          <span className="text-xs font-medium">
                            {option.label}
                          </span>
                          <span
                            className="text-[10px]"
                            style={{ color: colors.text.secondary }}
                          >
                            {scale.label}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </FadeIn>

            {/* Theme Selector */}
            <FadeIn direction="up" delay={200} duration={0.5}>
              <div>
                <h3
                  className="text-sm font-medium mb-6"
                  style={{ color: colors.text.primary }}
                >
                  Choose your theme
                </h3>
                <div className="flex overflow-x-auto gap-3 p-2 pb-4">
                  {themeOptions.map(themeOption => {
                    const isThemeActive = theme === themeOption.id;
                    const isHovered = hoveredTheme === themeOption.id;

                    return (
                      <button
                        key={themeOption.id}
                        className={`relative flex-shrink-0 flex flex-col items-center justify-center gap-1 rounded-md transition-all duration-200 ease-out overflow-hidden ${
                          isThemeActive ? 'border-2' : 'border'
                        }`}
                        style={{
                          height: 'calc(80px * var(--sizing-multiplier, 1))',
                          minWidth: 'calc(100px * var(--sizing-multiplier, 1))',
                          paddingBottom: isHovered ? 18 : 0,
                          ...(isThemeActive
                            ? {
                                backgroundColor: colors.active.background,
                                color: colors.active.text,
                                borderColor: colors.active.background,
                                transform: 'scale(1.05)',
                                boxShadow: shadows.interactive,
                              }
                            : isHovered
                              ? {
                                  backgroundColor: colors.hover.background,
                                  color: colors.hover.text,
                                  borderColor: colors.border.default,
                                  transform: 'scale(1.05)',
                                  boxShadow: shadows.interactive,
                                }
                              : {
                                  backgroundColor: 'transparent',
                                  color: colors.text.primary,
                                  borderColor: colors.border.default,
                                  transform: 'scale(1)',
                                  boxShadow: 'none',
                                }),
                        }}
                        onClick={() => setTheme(themeOption.id)}
                        onMouseEnter={() => setHoveredTheme(themeOption.id)}
                        onMouseLeave={() => setHoveredTheme(null)}
                      >
                        <div
                          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                          style={{ background: themeOption.preview }}
                        >
                          <Icon
                            name={themeOption.iconName}
                            size="sm"
                            color="white"
                            strokeWidth={2.5}
                            className="flex-shrink-0"
                          />
                        </div>
                        <span className="text-xs font-medium">
                          {themeOption.label}
                        </span>

                        {/* Variant dots — absolute row at bottom of tile, visible on hover */}
                        <div
                          className="absolute bottom-1.5 left-1/2 -translate-x-1/2 flex flex-row gap-1 transition-opacity duration-150"
                          style={{ opacity: isHovered ? 1 : 0 }}
                        >
                          {VARIANT_OPTIONS.map(v => {
                            const isVariantActive =
                              isThemeActive && variant === v.id;

                            return (
                              <div
                                key={v.id}
                                role="button"
                                title={v.label}
                                className="rounded-full flex-shrink-0"
                                style={{
                                  width: 10,
                                  height: 10,
                                  background: themeOption.preview,
                                  filter: v.filter,
                                  outline: isVariantActive
                                    ? `2px solid ${colors.primary}`
                                    : `1px solid ${withOpacity(colors.text.inverse, 0.4)}`,
                                  outlineOffset: isVariantActive ? 1 : 0,
                                }}
                                onClick={e => {
                                  e.stopPropagation();
                                  setTheme(themeOption.id);
                                  setVariant(v.id);
                                }}
                              />
                            );
                          })}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            </FadeIn>

            {/* Fun Style Selector */}
            <FadeIn direction="up" delay={250} duration={0.5}>
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h3
                    className="text-sm font-medium"
                    style={{ color: colors.text.primary }}
                  >
                    Choose your fun style
                  </h3>
                </div>
                <div className="flex overflow-x-auto gap-3 p-2 pb-4">
                  {getAllPlayfulThemes().map(playfulOption => {
                    const isActive = playfulTheme === playfulOption.name;

                    return (
                      <button
                        key={playfulOption.name}
                        className={`flex-shrink-0 flex flex-col items-center justify-center gap-1 rounded-md transition-all duration-200 ease-out hover:scale-105 ${
                          isActive ? 'border-2' : 'border'
                        }`}
                        style={{
                          height: 'calc(80px * var(--sizing-multiplier, 1))',
                          minWidth: 'calc(100px * var(--sizing-multiplier, 1))',
                          ...(isActive
                            ? {
                                backgroundColor: colors.active.background,
                                color: colors.active.text,
                                borderColor: colors.active.background,
                                transform: 'scale(1.05)',
                                boxShadow: shadows.interactive,
                              }
                            : {
                                backgroundColor: 'transparent',
                                color: colors.text.primary,
                                borderColor: colors.border.default,
                              }),
                        }}
                        onClick={() =>
                          setPlayfulTheme(
                            playfulOption.name as PlayfulThemeName
                          )
                        }
                      >
                        <span className="text-2xl">
                          {(() => {
                            const SvgMascot =
                              MASCOT_COMPONENTS[playfulOption.id];
                            return SvgMascot ? (
                              <SvgMascot />
                            ) : (
                              playfulOption.metadata.previewEmoji
                            );
                          })()}
                        </span>
                        <span className="text-xs font-medium">
                          {playfulOption.metadata.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </FadeIn>
          </div>

          {/* Fixed Footer with Logout */}
          <div
            className="flex-shrink-0 pt-4 border-t"
            style={{ borderColor: colors.border.default }}
          >
            <Button
              variant="destructive"
              className="w-full justify-center gap-2"
              onClick={handleLogout}
            >
              <Icon name={IconName.LOG_OUT} size="sm" />
              Logout
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Profile Picture Selector Modal */}
      <ProfilePictureSelector
        isOpen={showPictureSelector}
        onClose={() => setShowPictureSelector(false)}
        currentProfilePic={user.profile_image || null}
        onSelect={(url: string) => {
          updateUser({ profile_image: url });
        }}
      />
    </>
  );
}
